import React, { useContext, useEffect, useState } from 'react';
import {
    MDBContainer,
    MDBNavbar,
    MDBIcon
} from 'mdb-react-ui-kit';
import '../../stylesheet/bs-stepper.css'
import './SideNav.css';
import SiteInfo from './SiteInfo';
import SideNavStep from './SideNavStep';
import { AppContext, NavState } from '../App';
import UserControlService from '../../services/user-control-service';
import { DefaultEventService, NavEventService } from '../../services/event-service';
import TaskEditorPreview from './TaskEditorPreview';
import { Glyph } from 'konect-model';
import { Settings } from '../Settings';
import ProjectSettings from './ProjectSettings';

export enum NavEvent {
    TOGGLE_STATIC_SIDENAV = "TOGGLE_STATIC_SIDENAV",
    TOGGLE_IDEABOX = "TOGGLE_IDEABOX",
    TOGGLE_TABLE_GLYPHSKETCHING = "TOGGLE_TABLE_GLYPHSKETCHING"
}

interface SideNavProps {
    setShowProjectDialog: (state: boolean) => void;
    navState: NavState;
    onLoad: () => void;
}

export default function SideNav(props: SideNavProps) {
    const { project } = useContext(AppContext);
    const [showProjectSettings, setShowProjectSettings] = useState(false);
    const [showTaskModel] = useState(false);
    const [showIdeaBox, setShowIdeaBox] = useState(false);
    const [showDesignComposition, setShowDesignComposition] = useState(false);
    const [canDrawGlyphs, setCanDrawGlyphs] = useState(project.glyphs.length > 0);

    const [stateIsIdeaBox, setStateIsIdeaBox] = useState(!!(props.navState === NavState.IDEABOX_1 || props.navState === NavState.IDEABOX_2 || props.navState === NavState.GLYPH_1B));
    const [stateIsGlyph, setStateIsGlyph] = useState(!!(props.navState === NavState.GLYPH_1A || props.navState === NavState.GLYPH_2 || props.navState === NavState.GLYPH_3));

    useEffect(() => {
        DefaultEventService.on("GLYPHS_ADDED", (glyphs: Glyph[]) => {
            setCanDrawGlyphs(glyphs.length > 0);
        })
    }, [])

    useEffect(() => {
        setCanDrawGlyphs(project.glyphs.length > 0);
    }, [project])

    const changeView = (state : NavState) => {
        UserControlService.changeView(state);
    }

    // toggle static sidebar
    const [staticSidenav, setStaticSidenav] = useState(Settings.sideNav.isStatic);
	const toggleStaticSidenav = (status: boolean) => {
        setStaticSidenav(status);
        Settings.setNavbarStatic(status);
    }

    // toggle ideaBox
    const [ideaBoxEnabled, setIdeaBoxEnabled] = useState(Settings.sideNav.showIdeabox);
    const toggleIdeaBox = (status: boolean) => {
        setIdeaBoxEnabled(status);
        Settings.setNavbarShowIdeaBox(status);
        if (status && (props.navState === NavState.IDEABOX_1 || props.navState === NavState.IDEABOX_2)) changeView(NavState.TASK);
        else if (status && props.navState === NavState.GLYPH_1B) changeView(NavState.GLYPH_1A);
    }

    // toggle table glyph sketching (page setting)
    const [tableGlyphSketching, setTableGlyphSketching] = useState(Settings.sideNav.tableGlyphSketching);
    const toggleTableGlyphSketching = (status: boolean) => {
        setTableGlyphSketching(status);
        Settings.setTableGlyphSketching(status);
        setStateIsGlyph(false);
        changeView(status ? NavState.GLYPH_1B : NavState.GLYPH_1A);
    }

    useEffect(() => {
        NavEventService.on(NavEvent.TOGGLE_STATIC_SIDENAV, (status: boolean) => {toggleStaticSidenav(status)});
        NavEventService.on(NavEvent.TOGGLE_IDEABOX, (status: boolean) => {toggleIdeaBox(status)});
        NavEventService.on(NavEvent.TOGGLE_TABLE_GLYPHSKETCHING, (status: boolean) => {toggleTableGlyphSketching(status)});
    }, [])

    useEffect(() => {
        setStateIsIdeaBox(!!(props.navState === NavState.IDEABOX_1 || props.navState === NavState.IDEABOX_2));
        setStateIsGlyph(!!(props.navState === NavState.GLYPH_1A || props.navState === NavState.GLYPH_1B || props.navState === NavState.GLYPH_2 || props.navState === NavState.GLYPH_3));
    }, [props.navState, tableGlyphSketching])

    return (
        <>
        <MDBNavbar light bgColor='light' id='sidenav-stepper' className='d-flex align-items-start collapse flex-column' style={staticSidenav ? {transform: "translateX(0)"} : {marginRight: "-10.5rem"}}>
            <MDBContainer fluid className='justify-content-between flex-column flex-grow-1'>

                { /** Read for create project button
                <button onClick={() => { props.setShowProjectDialog(true) }}
                    type="button" className={"sidenav-create-button"} role="tab">
                    <span className="bs-stepper-label">NEW PROJECT</span>
                    <span className="bs-stepper-circle">
                        <MDBIcon fas icon='plus' />
                    </span>
                </button>  */}
                <div className="menu-content">

                {!!project.name && <div className="projectNameDiv" onClick={ () => setShowProjectSettings(true) }>
                    <span style={{width:"100%", overflow:"hidden", textOverflow:"ellipsis"}} title={project.name}>{project.name}</span>
                    <MDBIcon fas size='lg' icon="cog" />
                </div>}

                <div className='sidenav-horizontal-divider mt-2 mb-3'></div>
                    <div className="bs-stepper vertical align-self-end">
                        <div className="bs-stepper-header align-items-end" role="tablist">

                            {/*<!-- your steps here -->*/}

                            <SideNavStep label="Task Editor" image="./images/Icon_HTA.svg" isActive={props.navState === NavState.TASK}
                                onClick={() => {
                                    setShowIdeaBox(!showTaskModel);
                                    changeView(NavState.TASK);
                                }} />

                            <div className="line me-4" />
                            { ideaBoxEnabled && <>
                            <SideNavStep label="Idea Box" image="./images/Icon_IdeaBox.svg"
                                isActive={stateIsIdeaBox}
                                onClick={() => {
                                    setShowIdeaBox(!showIdeaBox);
                                    changeView(NavState.IDEABOX_1);
                                }} />

                            <div className="d-flex">
                                <div className='stepper-description'>
                                    <button onClick={() => {
                                        setShowIdeaBox(!showIdeaBox);
                                        changeView(NavState.IDEABOX_1)
                                    }} type="button"
                                        className={"step-trigger justify-content-end"
                                            + (props.navState === NavState.IDEABOX_1 ? " step-active" : "")}>
                                        1. Perception Box
                                    </button>
                                    <button onClick={() => {
                                        setShowIdeaBox(!showIdeaBox);
                                        changeView(NavState.IDEABOX_2)
                                    }} type="button"
                                        className={"step-trigger justify-content-end"
                                            + (props.navState === NavState.IDEABOX_2 ? " step-active" : "")}>
                                        2. Interaction Box
                                    </button>
                                </div>
                                <div className="line me-4" style={{ maxWidth: "1px" }}></div>
                            </div>
                            </>}

                            <SideNavStep label="Glyph Sketching" image="./images/Icon_GlyphSketching.svg"
                                isActive={stateIsGlyph}
                                onClick={() => {
                                    if (!tableGlyphSketching) changeView(NavState.GLYPH_1A);
                                    else if (canDrawGlyphs) changeView(NavState.GLYPH_2);
                                    else {
                                        changeView(NavState.GLYPH_1B);
                                        return;
                                    }
                                }}
                            />

                            <div className="d-flex">
                                <div className='stepper-description'>
                                    <button
                                        onClick={() => { changeView(tableGlyphSketching ? NavState.GLYPH_1B : NavState.GLYPH_1A) }}
                                        type="button"
                                        className={"step-trigger justify-content-end " + ((props.navState === NavState.GLYPH_1A || props.navState === NavState.GLYPH_1B) && "step-active")}>
                                        1. Create and Group
                                    </button>
                                    <button
                                        onClick={() => { changeView(NavState.GLYPH_2) }}
                                        type="button"
                                        disabled={!canDrawGlyphs}
                                        className={"step-trigger justify-content-end " + (props.navState === NavState.GLYPH_2 && "step-active")}>
                                        2. Sketch Glyphs
                                    </button>
                                    <button
                                        onClick={() => { changeView(NavState.GLYPH_3) }}
                                        type="button"
                                        disabled={!canDrawGlyphs}
                                        className={"step-trigger justify-content-end " + (props.navState === NavState.GLYPH_3 && "step-active")}>
                                        3. Compose Glyphs
                                        <span style={{fontStyle:"italic"}}> (WIP)</span>
                                    </button>
                                </div>
                                <div className="line me-4" style={{ maxWidth: "1px" }}></div>
                            </div>
                                    
                            <div className="step">
                                <button onClick={() => {
                                    setShowDesignComposition(!showDesignComposition);
                                    changeView(NavState.DESIGN);
                                }} type="button" className={"step-trigger justify-content-end" + (props.navState === NavState.DESIGN ? " step-active" : "")} role="tab">
                                    <span className="bs-stepper-label">Design Evaluation</span>
                                    <span className="bs-stepper-circle">
                                        <img src="./images/Icon_DesignComposition.svg" alt="Design Composition"></img>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='bottom-content' style={staticSidenav ? {opacity: 1} : {}}>
                    <TaskEditorPreview/>
                    <SiteInfo onUse={changeView} />
                    <div className="pinSideNav" onClick={() => toggleStaticSidenav(!staticSidenav)} style={staticSidenav ? {} : {color:"rgba(0, 0, 0, 0.25)"}}>
                        <MDBIcon fas icon="thumbtack" />
                    </div>
                </div>
            </MDBContainer>
        </MDBNavbar>
        <ProjectSettings showProjectSettings={showProjectSettings} setShowProjectSettings={setShowProjectSettings} onLoad={props.onLoad} />
        </>
    );
}